<template>
  <b-modal
    v-model="isOpen"
    has-modal-card
    trap-focus
    scroll="keep"
    aria-role="dialog"
    aria-label="ProfileModale"
    aria-modal
    class="ProfileModale"
    @cancel="emitCloseModale"
  >
    <div class="modal-card">
      <header class="modal-card-head">
        <H3TitleView v-if="isCurrentUserProfile" class="modal-card-title"
          >{{ $t("profilePage.myProfile-management") }}
        </H3TitleView>
        <H3TitleView v-else class="modal-card-title"
          >{{ $t("profilePage.title", [user.nom, user.prenom]) }}
        </H3TitleView>
      </header>
      <section class="modal-card-body">
        <b-tabs
          v-model="currentTabIndex"
          type="is-toggle"
          :animated="false"
          class="ProfileModale-tabs"
        >
          <!-- Onglet 1: Identité (toujours visible) -->
          <b-tab-item value="identity-tab" :label="$t('profilePage.identity')" :visible="true">
            <UserInfoDetails
              ref="userInfoDetails"
              :user="user"
              class="title-without-margin"
              :readonly="!canUserEditProfil"
              @close-modale="emitCloseModale"
            />
          </b-tab-item>

          <!-- Onglet 2: Mot de passe (conditionnel) -->
          <b-tab-item
            value="password-tab"
            :label="$t('input-password')"
            :visible="isCurrentUserProfile || canUserChangeOtherPwd"
          >
            <PasswordReset
              ref="resetPwd"
              :with-old-pwd="isCurrentUserProfile"
              :user="user"
              class="title-without-margin"
            />
          </b-tab-item>

          <!-- Onglet 3: Suppression (conditionnel) -->
          <b-tab-item
            value="delete-tab"
            :label="$t('profilePage.delete-info')"
            :visible="isCurrentUserProfile"
          >
            <div class="ProfileModale-delete-warning mb-4">
              <b-icon size="is-large" class="mr-2" icon="times-circle" type="is-danger" />
              {{ $t("profilePage.delete-warning") }}
            </div>
            <div v-if="doDeleteProfil">
              <InputWithValidation
                v-model="password"
                rules="required"
                type="password"
                password-reveal
                :placeholder="$t('input-password-tooltip')"
                :label="$t('profilePage.delete-account-confirmation')"
                vid="password"
              />
            </div>
          </b-tab-item>
        </b-tabs>
      </section>

      <footer class="modal-card-foot">
        <div class="buttons">
          <div class="hoverable-discrete-link mr-3" @click="emitCloseModale">
            {{ $t("close-generic") }}
          </div>

          <!-- Boutons de suppression (onglet suppression) -->
          <div v-if="showDeleteButtons">
            <b-button
              v-if="doDeleteProfil"
              :label="$t('profilePage.delete-account-confirm')"
              type="is-danger"
              rounded
              @click="deleteUserAccount"
            />
            <b-button
              v-else
              :label="$t('profilePage.delete')"
              type="is-danger"
              rounded
              @click="doDeleteProfil = true"
            />
          </div>

          <!-- Bouton de sauvegarde (autres onglets) -->
          <div v-else-if="showSaveButton">
            <b-button rounded type="is-primary" @click="saveAndClose">
              {{ $t("profilePage.save") }}
            </b-button>
          </div>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import type { ComponentPublicInstance } from "vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import UserInfoDetails from "@/views/profile/UserInfoDetails.vue"
import PasswordReset from "@/views/profile/PasswordReset.vue"
import H3TitleView from "@/views/commons/H3TitleView.vue"
import { UsersService } from "@/services/rest/users/UsersService"
import { LoginService } from "@/services/rest/login/LoginService"
import { PasswordCheckDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { BButton, BIcon, BModal, BTabs, BTabItem } from "buefy"

export default defineComponent({
  name: "ProfileModale",
  components: {
    InputWithValidation,
    UserInfoDetails,
    PasswordReset,
    H3TitleView,
    BButton,
    BIcon,
    BModal,
    BTabs,
    BTabItem,
  },
  props: {
    userId: { type: String, default: "" },
    idDossier: { type: String, default: "" },
    open: { type: Boolean, default: false },
  },

  emits: ["close-modale"],
  data() {
    return {
      loginService: LoginService.INSTANCE,
      userService: UsersService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      user: new UtilisateurDTO(),
      profils: PROFIL_UTILISATEUR,
      canUserEditProfil: false,
      isCurrentUserProfile: false,
      canUserChangeOtherPwd: false,
      currentTabIndex: "identity-tab",
      doDeleteProfil: false,
      password: "",
      previousUrl: "/users-list",
      previousTitle: "",
    }
  },
  computed: {
    isOpen: {
      get(): boolean {
        return this.open
      },
      set(value: boolean): void {
        if (!value) {
          this.emitCloseModale()
        }
      },
    },
    isOnDeleteTab(): boolean {
      return this.currentTabIndex === "delete-tab"
    },
    showDeleteButtons(): boolean {
      return this.canUserEditProfil && this.isOnDeleteTab
    },
    showSaveButton(): boolean {
      return this.canUserEditProfil && !this.isOnDeleteTab
    },
  },
  watch: {
    userId(): void {
      this.setUserProfile()
    },
  },
  mounted(): void {
    this.previousTitle = this.$t("profilePage.previous-title").toString()
    this.setUserProfile()
  },
  methods: {
    async setUserProfile(): Promise<void> {
      const loggedUser = this.loginService.getLoggedUser()
      if (this.userId) {
        this.user = await this.userService.getUserProfile(this.userId)
        if (this.user && loggedUser) {
          this.canUserChangeOtherPwd = loggedUser.profil === this.profils.SUPER_ADMINISTRATEUR
        }

        if (loggedUser && loggedUser.id == this.userId) {
          this.canUserEditProfil = true
          this.isCurrentUserProfile = true
        } else if (loggedUser && loggedUser.profil == this.profils.SUPER_ADMINISTRATEUR) {
          this.canUserEditProfil = true
          this.isCurrentUserProfile = false
        } else {
          this.canUserEditProfil = false
          this.isCurrentUserProfile = false
        }
      }
    },
    async saveAndClose(): Promise<void> {
      const userInfoComponent = this.$refs["userInfoDetails"] as ComponentPublicInstance & {
        submitProfile: () => Promise<string>
      }
      const resetPwdComponent = this.$refs["resetPwd"] as ComponentPublicInstance & {
        submitPassword: () => Promise<string>
      }

      //Chainage des deux enregistrements pour ne pas que le profil soit enregistré après le mot de passe
      //sinon le mdp n'ets pas modifié, cf. #12359

      await userInfoComponent.submitProfile().then((value: any) => {
        if (value !== "") {
          this.infoReportingService.error(value, new Error())
        } else {
          this.infoReportingService.success(
            this.$t("profilePage.profile-modification-ok").toString(),
          )
        }
      })

      await resetPwdComponent.submitPassword().then((value: any) => {
        if (value !== "") {
          this.infoReportingService.error(value, new Error())
        } else {
          this.infoReportingService.success(
            this.$t("profilePage.profile-modification-ok").toString(),
          )
        }
      })
    },
    emitCloseModale(): void {
      this.setUserProfile()
      this.$emit("close-modale")
    },
    deleteUserAccount(): void {
      if (!this.user || !this.user.id) {
        return
      }
      const passwordBean = new PasswordCheckDTO()
      passwordBean.motDePasse = this.password
      this.loginService
        .checkPassword(passwordBean)
        .then(() => this.userService.deleteUserAccount(this.user))
        .then(() => this.emitCloseModale())
        .then(() => this.loginService.logout())
        .then(() =>
          this.infoReportingService.success(this.$t("profilePage.account-deleted").toString()),
        )
        .catch((error) => {
          this.infoReportingService.error(
            this.$t("profilePage.authentication-error").toString(),
            error,
          )
        })
    },
  },
})
</script>

<style lang="scss">
.title-without-margin {
  .title-view {
    margin-top: 0;
  }
}

.ProfileModale {
  &.modal .modal-card {
    height: 75%;
    overflow-y: auto !important;
  }

  .animation-content {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }
}

.ProfileModale-tabs {
  &.b-tabs .tab-content {
    height: 100%;
    align-items: center;
    justify-content: center;
  }
}

.ProfileModale-delete-warning {
  display: flex;
  align-items: center;
}
</style>
